interface ApiResponse {
    status_code: number;
    status_message: string;
}

interface ApiErrorData {
    error_message: string;
    error_code: string;
}

interface ApiResponseWithError<T> extends ApiResponse {
    data: T | ApiErrorData;
}

export const isAPISuccessful = (response: ApiResponse): boolean => {
    return response.status_code === 0;
};

export const getAPIErrorMessage = (response: ApiResponse): string => {
    return response.status_message;
};

/**
 * Handles API responses that may contain errors in the response body
 * even when HTTP status is 200
 * @param response - The API response
 * @param successCallback - Function to call on success
 * @param errorCallback - Function to call on error
 * @returns Object with success status and data/error
 */
export const handleApiResponse = <T>(
    response: ApiResponseWithError<T>,
    successCallback?: (data: T) => void,
    errorCallback?: (error: string, errorCode?: string) => void
): { success: boolean; data?: T; error?: string; errorCode?: string } => {
    if (response.status_code === 0) {
        // Success case
        const data = response.data as T;
        successCallback?.(data);
        return { success: true, data };
    } else {
        // Error case - API returned error in response body
        const errorData = response.data as ApiErrorData;
        const errorMessage = errorData.error_message || response.status_message || 'API request failed';
        const errorCode = errorData.error_code;
        
        errorCallback?.(errorMessage, errorCode);
        
        // Log error details for debugging
        console.error('API error response:', {
            status_code: response.status_code,
            status_message: response.status_message,
            error_code: errorCode,
            error_message: errorMessage
        });
        
        return { 
            success: false, 
            error: errorMessage, 
            errorCode 
        };
    }
};
